import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { 
  Card, 
  CardHeader, 
  CardBody,
  Badge,
  Table,
  Button,
  PageLoader,
  Select
} from '../components/ui';
import { 
  Briefcase, 
  Users, 
  TrendingUp, 
  Clock,
  CheckCircle,
  UserCheck,
  Filter
} from 'lucide-react';

interface EmployerMetrics {
  _id: string;
  companyName: string;
  totalJobs: number;
  activeJobs: number;
  closedJobs: number;
  totalApplications: number;
  shortlisted: number;
  interviewed: number;
  hired: number;
}

interface JobMetrics {
  _id: string;
  title: string;
  companyName: string;
  status: string;
  totalApplicants: number;
  shortlisted: number;
  interviewed: number;
  hired: number;
  rejected: number;
  createdAt: string;
  applicationDeadline?: string;
  timeToFillDays: number | null;
}

interface RevenueMetrics {
  period: string;
  subscriptionRevenue: number;
  hireRevenue: number;
  totalRevenue: number;
  subscriptionPayments: number;
  hirePayments: number;
  topEmployers: Array<{
    companyName: string;
    totalJobs: number;
    totalApplications: number;
  }>;
  conversionRates: {
    totalApplicants: number;
    shortlistedRate: string;
    interviewRate: string;
    hireRate: string;
  };
}

const AdminRecruitmentMetrics: React.FC = () => {
  const [employerMetrics, setEmployerMetrics] = useState<EmployerMetrics[]>([]);
  const [jobMetrics, setJobMetrics] = useState<JobMetrics[]>([]);
  const [revenueMetrics, setRevenueMetrics] = useState<RevenueMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'employers' | 'jobs' | 'revenue'>('overview');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedEmployer) {
      fetchJobMetrics(selectedEmployer);
    } else {
      fetchJobMetrics();
    }
  }, [selectedEmployer]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employerRes, jobRes, revenueRes] = await Promise.all([
        adminAPI.getEmployerMetrics(),
        adminAPI.getJobMetrics(),
        adminAPI.getRevenueMetrics({ period: 30 })
      ]);
      setEmployerMetrics(employerRes.data.data.employers);
      setJobMetrics(jobRes.data.data.jobs);
      setRevenueMetrics(revenueRes.data.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobMetrics = async (employerId?: string) => {
    try {
      const params = employerId ? { employerId } : {};
      const res = await adminAPI.getJobMetrics(params);
      setJobMetrics(res.data.data.jobs);
    } catch (error) {
      console.error('Error fetching job metrics:', error);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (num: number) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'closed': return 'default';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const employerColumns = [
    {
      key: 'companyName',
      header: 'Company',
      render: (item: EmployerMetrics) => (
        <span className="font-medium text-secondary-900">{item.companyName}</span>
      )
    },
    {
      key: 'totalJobs',
      header: 'Total Jobs',
      width: '100px',
      render: (item: EmployerMetrics) => (
        <span className="font-medium">{item.totalJobs}</span>
      )
    },
    {
      key: 'activeJobs',
      header: 'Active',
      width: '80px',
      render: (item: EmployerMetrics) => (
        <Badge variant="success" size="sm">{item.activeJobs}</Badge>
      )
    },
    {
      key: 'totalApplications',
      header: 'Applicants',
      width: '100px',
      render: (item: EmployerMetrics) => formatNumber(item.totalApplications)
    },
    {
      key: 'shortlisted',
      header: 'Shortlisted',
      width: '100px',
      render: (item: EmployerMetrics) => (
        <span className="text-primary-600 font-medium">{item.shortlisted}</span>
      )
    },
    {
      key: 'interviewed',
      header: 'Interviewed',
      width: '100px',
      render: (item: EmployerMetrics) => (
        <span className="text-info-600 font-medium">{item.interviewed}</span>
      )
    },
    {
      key: 'hired',
      header: 'Hired',
      width: '80px',
      render: (item: EmployerMetrics) => (
        <Badge variant="success" size="sm">{item.hired}</Badge>
      )
    }
  ];

  const jobColumns = [
    {
      key: 'title',
      header: 'Job Title',
      render: (item: JobMetrics) => (
        <div>
          <p className="font-medium text-secondary-900">{item.title}</p>
          <p className="text-xs text-secondary-500">{item.companyName}</p>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (item: JobMetrics) => (
        <Badge variant={getStatusBadgeVariant(item.status)}>{item.status}</Badge>
      )
    },
    {
      key: 'totalApplicants',
      header: 'Applicants',
      width: '100px',
      render: (item: JobMetrics) => formatNumber(item.totalApplicants)
    },
    {
      key: 'shortlisted',
      header: 'Shortlisted',
      width: '100px',
      render: (item: JobMetrics) => (
        <span className="text-primary-600">{item.shortlisted}</span>
      )
    },
    {
      key: 'interviewed',
      header: 'Interviewed',
      width: '100px',
      render: (item: JobMetrics) => (
        <span className="text-info-600">{item.interviewed}</span>
      )
    },
    {
      key: 'hired',
      header: 'Hired',
      width: '80px',
      render: (item: JobMetrics) => (
        <Badge variant="success" size="sm">{item.hired}</Badge>
      )
    },
    {
      key: 'timeToFillDays',
      header: 'Time to Fill',
      width: '100px',
      render: (item: JobMetrics) => (
        item.timeToFillDays ? (
          <span className="flex items-center gap-1 text-secondary-600">
            <Clock className="w-3 h-3" />
            {item.timeToFillDays} days
          </span>
        ) : (
          <span className="text-secondary-400">-</span>
        )
      )
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <PageLoader message="Loading recruitment metrics..." />
      </AdminLayout>
    );
  }

  const totals = employerMetrics.reduce((acc, e) => ({
    totalJobs: acc.totalJobs + e.totalJobs,
    totalApplications: acc.totalApplications + e.totalApplications,
    totalHired: acc.totalHired + e.hired,
    totalShortlisted: acc.totalShortlisted + e.shortlisted,
    totalInterviewed: acc.totalInterviewed + e.interviewed
  }), { totalJobs: 0, totalApplications: 0, totalHired: 0, totalShortlisted: 0, totalInterviewed: 0 });

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-secondary-900">Recruitment Metrics</h1>
          <p className="text-secondary-500 mt-1">
            Monitor hiring progress and revenue from employer activities
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-secondary-200">
          {(['overview', 'employers', 'jobs', 'revenue'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="bg-gradient-to-br from-primary-500 to-primary-600 text-white">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-primary-100">Total Jobs</p>
                      <p className="text-2xl font-bold">{totals.totalJobs}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card className="bg-gradient-to-br from-info-500 to-info-600 text-white">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-info-100">Total Applicants</p>
                      <p className="text-2xl font-bold">{formatNumber(totals.totalApplications)}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card className="bg-gradient-to-br from-warning-500 to-warning-600 text-white">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-warning-100">Shortlisted</p>
                      <p className="text-2xl font-bold">{totals.totalShortlisted}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card className="bg-gradient-to-br from-success-500 to-success-600 text-white">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-success-100">Hired</p>
                      <p className="text-2xl font-bold">{totals.totalHired}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card className="bg-gradient-to-br from-secondary-600 to-secondary-700 text-white">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-secondary-200">Revenue (30d)</p>
                      <p className="text-2xl font-bold">{revenueMetrics ? formatCurrency(revenueMetrics.totalRevenue) : '$0'}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Conversion Rates */}
            {revenueMetrics && (
              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-secondary-900">Application Conversion Rates</h3>
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-secondary-900">
                        {revenueMetrics.conversionRates.totalApplicants}
                      </div>
                      <p className="text-sm text-secondary-500">Total Applicants</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">
                        {revenueMetrics.conversionRates.shortlistedRate}%
                      </div>
                      <p className="text-sm text-secondary-500">Shortlisted Rate</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-info-600">
                        {revenueMetrics.conversionRates.interviewRate}%
                      </div>
                      <p className="text-sm text-secondary-500">Interview Rate</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-success-600">
                        {revenueMetrics.conversionRates.hireRate}%
                      </div>
                      <p className="text-sm text-secondary-500">Hire Rate</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        )}

        {/* Employers Tab */}
        {activeTab === 'employers' && (
          <Card padding="none">
            <CardHeader className="p-4 border-b border-secondary-100">
              <h3 className="text-lg font-semibold text-secondary-900">
                Employer Performance
              </h3>
            </CardHeader>
            <CardBody className="p-0">
              <Table
                columns={employerColumns}
                data={employerMetrics}
                keyExtractor={(item) => item._id}
                striped
                emptyMessage="No employer data available"
              />
            </CardBody>
          </Card>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-secondary-500" />
                <span className="text-sm text-secondary-600">Filter by employer:</span>
              </div>
              <select
                value={selectedEmployer}
                onChange={(e) => setSelectedEmployer(e.target.value)}
                className="px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Employers</option>
                {employerMetrics.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.companyName}
                  </option>
                ))}
              </select>
            </div>

            <Card padding="none">
              <CardHeader className="p-4 border-b border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Job Performance
                </h3>
              </CardHeader>
              <CardBody className="p-0">
                <Table
                  columns={jobColumns}
                  data={jobMetrics}
                  keyExtractor={(item) => item._id}
                  striped
                  emptyMessage="No job data available"
                />
              </CardBody>
            </Card>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && revenueMetrics && (
          <div className="space-y-6">
            {/* Revenue Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardBody className="text-center py-6">
                  <p className="text-sm text-secondary-500 mb-2">Subscription Revenue</p>
                  <p className="text-3xl font-bold text-primary-600">
                    {formatCurrency(revenueMetrics.subscriptionRevenue)}
                  </p>
                  <p className="text-xs text-secondary-400 mt-1">
                    {revenueMetrics.subscriptionPayments} payments
                  </p>
                </CardBody>
              </Card>
              <Card>
                <CardBody className="text-center py-6">
                  <p className="text-sm text-secondary-500 mb-2">Per-Hire Revenue</p>
                  <p className="text-3xl font-bold text-success-600">
                    {formatCurrency(revenueMetrics.hireRevenue)}
                  </p>
                  <p className="text-xs text-secondary-400 mt-1">
                    {revenueMetrics.hirePayments} hires
                  </p>
                </CardBody>
              </Card>
              <Card className="bg-gradient-to-br from-primary-500 to-primary-600 text-white">
                <CardBody className="text-center py-6">
                  <p className="text-sm text-primary-100 mb-2">Total Revenue</p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(revenueMetrics.totalRevenue)}
                  </p>
                  <p className="text-xs text-primary-100 mt-1">
                    Last {revenueMetrics.period}
                  </p>
                </CardBody>
              </Card>
            </div>

            {/* Top Employers */}
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-secondary-900">
                  Top Performing Employers
                </h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {revenueMetrics.topEmployers.map((emp, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-secondary-900">{emp.companyName}</p>
                          <p className="text-xs text-secondary-500">
                            {emp.totalJobs} jobs · {emp.totalApplications} applicants
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRecruitmentMetrics;
